import { useCallback } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';

import {
  ExportOptions,
  setExportInvoicesReportType,
  setInvoiceExportMode,
  setShowExportInvoicesModal,
  setSkipExportGatherDataStep,
} from './invoice.slice';
import { useLazyFetchInvoiceExportData } from './modals/DownloadInvoices/useLazyFetchInvoiceExportData';

// Unified handler to export invoices by invoice id
export function useExportInvoices(
  exportMode: 'BLOB' | 'DOWNLOAD' = 'DOWNLOAD'
) {
  const { trigger, loading } = useLazyFetchInvoiceExportData();
  const dispatch = useAppDispatch();

  const handleExportInvoiceAsQuickbooksCSV = useCallback(
    async (invoices: string[]) => {
      await trigger({
        selectedInvoices: invoices,
        format: 'csv',
      });
    },
    [trigger]
  );

  const handleExportInvoiceAsCondensedPDF = useCallback(
    async (invoices: string[]) => {
      await trigger({
        selectedInvoices: invoices,
        format: 'pdf',
        outputType: 'condensed',
      });

      dispatch(setExportInvoicesReportType('condensed'));
      dispatch(setSkipExportGatherDataStep(true));
      dispatch(setShowExportInvoicesModal(true));
      dispatch(setInvoiceExportMode(exportMode));
    },
    [dispatch, exportMode, trigger]
  );

  const handleExport = useCallback(
    async (type: ExportOptions, invoices: string[]) => {
      if (type === 'quickbooks') {
        await handleExportInvoiceAsQuickbooksCSV(invoices);
      } else {
        await handleExportInvoiceAsCondensedPDF(invoices);
      }
    },
    [handleExportInvoiceAsCondensedPDF, handleExportInvoiceAsQuickbooksCSV]
  );

  return {
    handleExport,
    isLoading: loading,
  };
}
